import { useState, useEffect, useRef } from 'react';
import { FEATURES } from '@/config/environment';

/**
 * Simple in-memory cache keyed by a caller-provided cache key.
 * Prevents redundant network calls across re-renders and
 * multiple components requesting the same data.
 */
const cache = new Map<string, { data: unknown; timestamp: number }>();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Hook that fetches CMS content with automatic fallback to hardcoded defaults.
 *
 * - On initial render, returns `fallback` immediately (zero delay).
 * - If CMS is enabled (`FEATURES.CMS_CONTENT_ENABLED`), fires `fetchFn` in
 *   a useEffect and replaces state on success.
 * - If the fetch fails, keeps `fallback` and logs a console warning.
 * - Results are cached in memory for 5 minutes to avoid redundant calls.
 *
 * @param cacheKey  Stable string used to de-duplicate / cache results
 * @param fetchFn   Async function that returns CMS data
 * @param fallback  Hardcoded default data (returned immediately and used as fallback)
 */
export function useCMSContent<T>(
  cacheKey: string,
  fetchFn: () => Promise<T>,
  fallback: T,
): { data: T; isFromCMS: boolean } {
  const [data, setData] = useState<T>(fallback);
  const [isFromCMS, setIsFromCMS] = useState(false);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!FEATURES.CMS_CONTENT_ENABLED) return;
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    // Check cache first
    const cached = cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
      setData(cached.data as T);
      setIsFromCMS(true);
      return;
    }

    let cancelled = false;

    fetchFn()
      .then((result) => {
        if (cancelled) return;
        cache.set(cacheKey, { data: result, timestamp: Date.now() });
        setData(result);
        setIsFromCMS(true);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn(`[CMS] Failed to fetch "${cacheKey}", using hardcoded fallback:`, err);
      });

    return () => {
      cancelled = true;
    };
  }, [cacheKey, fetchFn, fallback]);

  return { data, isFromCMS };
}
